import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Map as MapIcon, PackageSearch, UserRound } from 'lucide-react';
import { NpcCard } from '../progression/NpcCard';
import { ValidationBadge } from '../progression/ValidationBadge';
import { EntityThumb } from './EntityThumb';
import { itemName, itemPath, mapPath, monsterPath } from '../../utils/worldFormat';
import { locationPoints, priceLabel, publishedShopItems, serviceLabel, shopOf } from '../../utils/npcShops';
import { NPC_PATH } from '../../utils/npcShops';
import { NpcQuestServices } from './NpcQuestServices';
import { SHOP_INVENTORY_GAP } from '../../data/guide/technicalAudit';
import type { NpcLocation } from '../../utils/npcLocation';
import type { Npc } from '../../types/progression';
import type { WorldData } from '../../types/world';
const btn = 'inline-flex h-9 items-center gap-1.5 rounded-xl border border-white/10 px-3 text-xs font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/40 hover:text-white';

/** Ficha de NPC en "NPC y tiendas": misma tarjeta de ubicación que el resto de la guía + servicios + tienda. */
export function NpcShopCard({
  npc,
  loc,
  w
}: {
  npc: Npc;
  loc: NpcLocation;
  w?: WorldData;
}) {
  const mapId = locationPoints(loc).find(p => p.mapId !== undefined)?.mapId;
  const shop = shopOf(npc.id);
  const sells = npc.services?.some(s => s.kind === 'venta');
  const items = shop ? publishedShopItems(w, shop) : [];
  const ownGuide = npc.sectionPath.startsWith(NPC_PATH) ? undefined : npc.sectionPath;
  return <article id={npc.id} className="scroll-mt-24 md:scroll-mt-32" data-id="element-1746" data-mp="2pdw309usu3zf">
      <NpcCard npcId={npc.id} data-id="element-1747" data-mp="2uc7d8lp0agi6" />

      <div className="mt-2.5 space-y-3 px-1" data-id="element-1748" data-mp="2uaw2u3122ai3">
        {npc.identityNote && <p className="rounded-xl bg-amber-300/[0.06] px-3 py-2 text-xs text-amber-100" data-id="element-1749" data-mp="2yeaphcji1p4i">{npc.identityNote}</p>}

        {npc.services?.length ? <div data-id="element-1750" data-mp="9oawv0ori8me">
            <p className="mb-1.5 text-xs font-semibold text-gray-400" data-id="element-1751" data-mp="dntglt4ukdnn">Servicios</p>
            <ul className="flex flex-wrap gap-1.5" data-id="element-1752" data-mp="dpwf71k2ff3z">
              {npc.services.map(s => <li key={s.kind} className="flex flex-wrap items-center gap-1.5 rounded-xl bg-white/[0.04] px-2.5 py-1.5 text-sm text-white" data-id="element-1753" data-mp="2aqk6fwgrxwyw">
                  {serviceLabel[s.kind]}
                  {s.note && <span className="text-xs text-gray-400" data-id="element-1754" data-mp="2jn1l39ockcze">· {s.note}</span>}
                  <ValidationBadge status={s.status} label={s.status === 'clasica' ? 'Función clásica' : undefined} data-id="element-1755" data-mp="2ercfaolqhzun" />
                </li>)}
            </ul>
          </div> : null}

        <NpcQuestServices w={w} monsterId={npc.monsterId} data-id="element-1756" data-mp="1n0g5jwuz0bbl" />

        {(sells || shop) && <div data-id="element-1757" data-mp="20r1p7ns90paj">
            <p className="mb-1.5 text-xs font-semibold text-gray-400" data-id="element-1758" data-mp="20rkwmak9vvab">Qué vende</p>
            {items.length ? <ul className="grid gap-1.5 sm:grid-cols-2" data-id="element-1759" data-mp="1vxgsdz2e61cx">
                {items.map(it => <li key={`${it.itemId}-${it.level ?? ''}`} className="flex items-center gap-2.5 rounded-xl bg-white/[0.03] px-2.5 py-2" data-id="element-1760" data-mp="187w7hamogu10">
                    <EntityThumb entity={{
              type: 'item',
              id: it.itemId
            }} name={w ? itemName(w, it.itemId) : it.itemId} placeholder data-id="element-1761" data-mp="3exzgy2mzu5xh" />
                    <span className="min-w-0 flex-1" data-id="element-1762" data-mp="3jqis6sy7koz4">
                      <Link to={itemPath(it.itemId)} className="block truncate text-sm font-semibold text-white underline decoration-white/20 underline-offset-4 hover:text-[#D4AF37]" data-id="element-1763" data-mp="35y2zakz0u7bx">
                        {w ? itemName(w, it.itemId) : it.itemId}
                      </Link>
                      <span className="block text-xs text-gray-400" data-id="element-1764" data-mp="6ifrlepg17np">
                        {[it.level !== undefined ? `+${it.level}` : '', priceLabel(it) ?? 'Precio no documentado'].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </li>)}
              </ul> : <p className="flex items-start gap-2 text-sm text-gray-400" data-id="element-1765" data-mp="2d7uc2epmowqh">
                <PackageSearch className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1766" data-mp="2i8i330duxdkp" />
                {SHOP_INVENTORY_GAP} La exportación del 08/10/2026 no incluye tiendas; esto no significa que no venda nada.
              </p>}
          </div>}

        <div className="flex flex-wrap gap-2" data-id="element-1767" data-mp="2opbi4k4vomq7">
          {mapId !== undefined && <Link to={mapPath(mapId)} className={btn} data-id="element-1768" data-mp="2omxnudi0vejp">
              <MapIcon className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1769" data-mp="2jra1e5uxczhw" /> Ver mapa
            </Link>}
          {npc.monsterId !== undefined && <Link to={monsterPath(npc.monsterId)} className={btn} data-id="element-1770" data-mp="3cf5ul0lac99x">
              <UserRound className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1771" data-mp="3ccrxynayip09" /> Ver ficha
            </Link>}
          {ownGuide && <Link to={ownGuide} className={btn} data-id="element-1772" data-mp="32hhvjygncfly">
              <BookOpen className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1773" data-mp="32it389d7q7ic" /> Ver guía
            </Link>}
        </div>
      </div>
    </article>;
}